import { Button, Chips, Css, SubmitButton, useComputed, useModal, useTestIds } from "@homebound/beam";
import { useFormState } from "@homebound/form-state";
import { useEffect } from "react";
import { useTakeoffsBulkEditPanel_GetTlisQuery } from "src/routes/libraries/plan-package-v2/components/takeoffs/side-panel/TakeoffsBulkEditPanel.generated";
import { TakeoffsSidePanel } from "src/routes/libraries/plan-package-v2/components/takeoffs/side-panel/TakeoffsSidePanel";
import { useTakeoffsSidePanel } from "src/routes/libraries/plan-package-v2/components/takeoffs/side-panel/useTakeoffsSidePanel";
import {
  BulkEditFields,
  bulkEditFormConfig,
  saveBulkEdit,
} from "src/routes/libraries/plan-package/takeoffs/components/BulkEditFields";
import { DeleteItemsConfirmationModal } from "src/routes/libraries/plan-package/takeoffs/components/DeleteItemsConfirmationModal";
import {
  useSelectedTliIds,
  useTakeoffsManagerContext,
  useTakeoffsStore,
} from "src/routes/libraries/plan-package/takeoffs/TakeoffsManagerContext";
import { queryResult } from "src/utils";

/** Bulk edits the table's selection, following it as rows are toggled. Only changed fields are applied. */
export function TakeoffsBulkEditPanel() {
  const store = useTakeoffsManagerContext();
  const planPackageId = useTakeoffsStore((state) => state.planPackage.id);
  const versionId = useTakeoffsStore((state) => state.planPackage.version.id);
  const itemTableApi = useTakeoffsStore((state) => state.itemTableApi);
  const selectedTliIds = useSelectedTliIds(itemTableApi);
  const { closePanel, dismissPanel, registerDirtyCheck } = useTakeoffsSidePanel();
  const { openModal } = useModal();
  const tid = useTestIds({}, "takeoffsBulkEditPanel");

  const query = useTakeoffsBulkEditPanel_GetTlisQuery({
    variables: { filter: { id: selectedTliIds, readyPlan: [planPackageId] }, versionId },
    skip: selectedTliIds.isEmpty,
  });
  // Lags the selection while a toggle re-queries, so Save waits for `loading` to clear
  const tlis = (query.data ?? query.previousData)?.takeoffLineItemsPage.items ?? [];

  const formState = useFormState({ config: bulkEditFormConfig });
  useEffect(() => registerDirtyCheck(() => formState.dirty), [registerDirtyCheck, formState]);
  // `SubmitButton` lets a form without an `id` save untouched, as if it were a new entity
  const isDirty = useComputed(() => formState.dirty, [formState]);

  // Clearing the selection, or a delete (whose save clears it), leaves nothing to edit
  const isEmpty = selectedTliIds.isEmpty;
  useEffect(() => {
    if (isEmpty) dismissPanel();
  }, [isEmpty, dismissPanel]);

  async function save() {
    await saveBulkEdit({ formState, saveTli: store.getState().saveTli, tlis });
    dismissPanel();
  }

  const costCodes = tlis.map((tli) => tli.item.costCode.displayName).unique();

  return (
    // TODO: Beam gap (DS-419): swap for Beam's `RightPanePanel` once its isDirty guard lands
    // (homebound-team/beam#1533); its footer actions (DS-410) already cover Delete (N), Cancel and Save
    <TakeoffsSidePanel
      title="Bulk Edit"
      onClose={closePanel}
      footer={{
        start: (
          <Button
            variant="tertiaryDanger"
            icon="trash"
            label={`Delete (${selectedTliIds.length})`}
            onClick={() =>
              openModal({ content: <DeleteItemsConfirmationModal store={store} itemIdsToDelete={selectedTliIds} /> })
            }
            {...tid.delete}
          />
        ),
        end: (
          <>
            <Button variant="secondary" label="Cancel" onClick={closePanel} />
            <SubmitButton
              label="Save"
              form={formState}
              disabled={query.loading || (!isDirty && "Change a field to save")}
              onClick={save}
            />
          </>
        ),
      }}
    >
      {queryResult(query, () => (
        <div css={Css.df.fdc.gap2.$}>
          <div {...tid.costCodes}>
            <Chips values={costCodes} />
          </div>
          <BulkEditFields store={store} formState={formState} tlis={tlis} />
        </div>
      ))}
    </TakeoffsSidePanel>
  );
}
